import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { stackName, type DeployPreviewDTO, type DeployRequest, type JobDTO, type ProjectDTO, type VpsDTO } from '@devrobot/shared';
import { GitBranch, Lock, Rocket, Server } from 'lucide-react';
import { ApiError, api, newIdempotencyKey, seg } from '../../../api/client';
import { qk, useApiMutation, useBranches, useEnvironments, useManifest, useVpsList } from '../../../api/queries';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { EmptyState } from '../../../components/EmptyState';
import { ErrorAlert } from '../../../components/ErrorAlert';
import { Section } from '../../../components/PageHeader';
import { RelativeTime } from '../../../components/RelativeTime';
import { SkeletonLines } from '../../../components/Skeleton';
import { useToast } from '../../../components/Toaster';
import { Haptic } from '../../../kit/haptics';
import { haptic } from '../../../kit/native';
import { useDebounced } from '../../../hooks/useDebounced';
import { shortSha } from '../../../lib/format';
import { DeployPreviewPanel } from './DeployPreviewPanel';
import { PortsEditor, ResourcesEditor, RoutesEditor, portComplete, routeComplete, servicesOf, type Port, type Resources, type Route } from './DeploySettings';

const vpsSelectable = (v: VpsDTO) => v.enrollmentState === 'ready' && (v.connection === 'connected' || v.connection === 'stale') && !v.deploymentsDisabled && !v.maintenance;

function vpsLabel(v: VpsDTO): string {
  const notes: string[] = [v.connection === 'connected' ? 'connected' : v.connection.replace('_', ' ')];
  if (v.enrollmentState !== 'ready') notes.push(`enrollment ${v.enrollmentState.replace('_', ' ')}`);
  if (v.deploymentsDisabled) notes.push('deployments disabled');
  if (v.maintenance) notes.push('maintenance');
  return `${v.name} — ${notes.join(', ')}`;
}

interface Settings {
  key: string;
  routes: Route[];
  ports: Port[];
  resources: Resources;
}

export function DeployTab({ project }: { project: ProjectDTO }) {
  const key = project.key;
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const branches = useBranches(key, project.deployable);
  const envs = useEnvironments(key);
  const vpsList = useVpsList();
  const manifest = useManifest(key);

  const [branch, setBranch] = useState(params.get('branch') ?? '');
  const [env, setEnv] = useState(params.get('env') ?? '');
  const [vpsId, setVpsId] = useState(params.get('vps') ?? '');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState<{ field: string; expected?: string; message: string } | null>(null);

  // Sensible defaults once the lists load: default branch, first deployable environment, first ready VPS.
  useEffect(() => {
    if (!branch && branches.data?.length) setBranch((branches.data.find((b) => b.default) ?? branches.data[0]).name);
  }, [branches.data, branch]);
  useEffect(() => {
    if (!env && envs.data?.length) setEnv((envs.data.find((e) => e.can.deploy && e.name === 'development') ?? envs.data.find((e) => e.can.deploy) ?? envs.data[0]).name);
  }, [envs.data, env]);
  useEffect(() => {
    if (!vpsId && vpsList.data?.length) {
      const ok = vpsList.data.find(vpsSelectable);
      if (ok) setVpsId(ok.id);
    }
  }, [vpsList.data, vpsId]);

  const selection = `${env}|${vpsId}`;
  const current = settings?.key === selection ? settings : null;
  const envDTO = envs.data?.find((e) => e.name === env);
  const branchDTO = branches.data?.find((b) => b.name === branch);
  const vpsDTO = vpsList.data?.find((v) => v.id === vpsId);

  const request = useMemo<DeployRequest | null>(() => {
    if (!branch || !env || !vpsId) return null;
    const base: DeployRequest = { environment: env, vpsId, branch };
    if (!current) return base;
    return { ...base, routes: current.routes.filter(routeComplete), ports: current.ports.filter(portComplete), resources: current.resources };
  }, [branch, env, vpsId, current]);
  const debounced = useDebounced(request, 450);
  const requestKey = JSON.stringify(request);
  const upToDate = requestKey === JSON.stringify(debounced);

  const preview = useQuery({
    queryKey: ['deploy-preview', key, debounced],
    queryFn: () => api.post<DeployPreviewDTO>(`/api/v1/projects/${seg(key)}/deployments/preview`, debounced),
    enabled: Boolean(debounced),
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
  const p = preview.data && preview.data.environment.name === env && preview.data.vps.id === vpsId ? preview.data : undefined;

  // First preview for a selection seeds the editors: the existing target's settings, or suggestions for a new target.
  useEffect(() => {
    if (!p || settings?.key === selection) return;
    setSettings({
      key: selection,
      routes: p.existing ? p.existing.routes : p.suggested.routes,
      ports: p.existing ? p.existing.ports : p.suggested.ports,
      resources: p.existing ? p.existing.resources : {},
    });
  }, [p, selection, settings?.key]);

  // A changed request is a new intent: new idempotency key, replacement must be re-acknowledged.
  const idemKey = useRef(newIdempotencyKey());
  useEffect(() => {
    idemKey.current = newIdempotencyKey();
  }, [requestKey]);
  useEffect(() => setConfirmReplace(false), [selection, branch]);

  const submit = useApiMutation(
    (body: DeployRequest) => api.post<{ job: JobDTO; existing: boolean }>(`/api/v1/projects/${seg(key)}/deployments`, body, { headers: { 'Idempotency-Key': idemKey.current } }),
    { invalidate: [['jobs'], qk.projectJobs(key), qk.targets(key)] },
  );

  const services = servicesOf(manifest.data?.manifest, [...(current?.routes ?? []).map((r) => r.service), ...(current?.ports ?? []).map((x) => x.service), ...Object.keys(current?.resources ?? {})]);
  const incomplete = (current?.routes.some((r) => !routeComplete(r)) ?? false) || (current?.ports.some((x) => !portComplete(x)) ?? false);
  // Hostnames generated from the VPS domain (the suggestion for this selection, or the target's current ones).
  const defaultHosts = p ? [...p.suggested.routes, ...(p.existing?.routes ?? [])].filter((r) => r.generated).map((r) => r.hostname) : [];

  const deploy = (extra: Partial<DeployRequest> = {}) => {
    if (!request || !p) {
      haptic('error');
      return;
    }
    const body: DeployRequest = {
      ...request,
      expectedSha: p.branch?.commitSha,
      confirmReplace: confirmReplace || undefined,
      source: 'ui',
      ...extra,
    };
    submit.mutate(body, {
      onSuccess: (r) => {
        const action = { label: 'View job', onClick: () => navigate(`/jobs/${r.job.id}`) };
        if (r.existing) toast.info('Already submitted', { description: 'This deployment was already submitted; showing the existing job.', action });
        else toast.success('Deployment submitted', { description: 'It keeps running if you leave this page.', action });
        navigate(`/jobs/${r.job.id}`);
      },
      onError: (err) => {
        if (err instanceof ApiError && err.confirmation) setNeedsConfirmation({ ...err.confirmation, message: err.message });
        else haptic('error');
      },
    });
  };

  if (!project.deployable) {
    return (
      <EmptyState icon={GitBranch} title="Nothing to deploy yet">
        The GitLab repository has no branch. Run <code className="font-mono">devrobot push</code> from the project directory; the project becomes deployable once a branch exists.
      </EmptyState>
    );
  }

  const canDeployHere = envDTO?.can.deploy ?? false;
  const blocked = !p || !p.canDeploy || !upToDate || preview.isFetching || (p.replacesDifferentRelease && !confirmReplace) || !canDeployHere || incomplete || submit.isPending;
  const configHref = `?tab=config&env=${encodeURIComponent(env)}`;

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
      <div className="min-w-0 space-y-6 xl:col-span-2">
        <Section title="What to deploy" description="The branch is resolved to an exact commit; images are built by GitLab CI (or reused when a pipeline already built that commit).">
          {branches.isError ? <ErrorAlert error={branches.error} onRetry={() => void branches.refetch()} /> : null}
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Branch</legend>
            {branches.isPending ? (
              <div className="skeleton h-10 w-full" />
            ) : (
              <select className="select w-full font-mono" aria-label="Branch" value={branch} onChange={(e) => setBranch(e.target.value)}>
                {(branches.data ?? []).map((b) => (
                  <option key={b.name} value={b.name}>
                    {b.name}
                    {b.default ? ' (default)' : ''}
                    {b.protected ? ' (protected)' : ''}
                  </option>
                ))}
              </select>
            )}
            {branchDTO ? (
              <div className="label flex-wrap whitespace-normal">
                <span className="font-mono">{shortSha(branchDTO.commitSha)}</span>
                <span className="text-base-content/75">{branchDTO.commitTitle}</span>
                <RelativeTime value={branchDTO.committedAt} />
                {branchDTO.protected ? <span className="badge badge-ghost badge-xs">protected in GitLab</span> : null}
                {branchDTO.default ? <span className="badge badge-ghost badge-xs">default</span> : null}
              </div>
            ) : null}
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Environment</legend>
            {envs.isPending ? (
              <div className="skeleton h-10 w-full" />
            ) : (
              <select className="select w-full" aria-label="Environment" value={env} onChange={(e) => setEnv(e.target.value)}>
                {(envs.data ?? []).map((e) => (
                  <option key={e.id} value={e.name}>
                    {e.name}
                    {e.protected ? ' (protected)' : ''}
                    {e.can.deploy ? '' : ' — no deploy permission'}
                  </option>
                ))}
              </select>
            )}
            {envDTO ? (
              <div className="label flex-wrap whitespace-normal">
                {envDTO.protected ? (
                  <span className="badge badge-warning badge-soft badge-xs gap-1">
                    <Lock className="size-3" aria-hidden="true" /> protected
                  </span>
                ) : null}
                <span>{envDTO.branchRuleDescription}</span>
              </div>
            ) : null}
            {envDTO && !envDTO.can.deploy ? (
              <p role="note" className="text-sm dr-ink-error">
                Your GitLab role cannot deploy to {envDTO.name}{envDTO.protected ? ' (protected environments need Maintainer)' : ''}.
              </p>
            ) : null}
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">VPS</legend>
            {vpsList.isPending ? (
              <div className="skeleton h-10 w-full" />
            ) : (vpsList.data ?? []).length === 0 ? (
              <div role="note" className="alert alert-warning alert-soft text-sm">
                <Server className="size-4" aria-hidden="true" />
                No VPS is available to this project. A platform administrator must allow this project on a VPS.
              </div>
            ) : (
              <select className="select w-full" aria-label="VPS" value={vpsId} onChange={(e) => setVpsId(e.target.value)}>
                {!vpsId ? <option value="">Choose a VPS…</option> : null}
                {(vpsList.data ?? []).map((v) => (
                  <option key={v.id} value={v.id} disabled={!vpsSelectable(v)}>
                    {vpsLabel(v)}
                  </option>
                ))}
              </select>
            )}
            {vpsDTO ? (
              <p className="label whitespace-normal">
                {vpsDTO.host}
                {vpsDTO.domain ? (
                  <>
                    {' '}
                    · domain <span className="font-mono">{vpsDTO.domain}</span>
                  </>
                ) : null}{' '}
                · {vpsDTO.targets.length} application{vpsDTO.targets.length === 1 ? '' : 's'} · last observed <RelativeTime value={vpsDTO.observedAt} />
                {vpsDTO.connection === 'stale' ? <span className="badge badge-warning badge-soft badge-xs">stale observation</span> : null}
              </p>
            ) : null}
            <p className="label whitespace-normal">Disconnected, disabled or not-ready servers cannot be selected.</p>
          </fieldset>
        </Section>

        <Section title="Target settings" description={p?.existing ? 'Prefilled from the running target. Changes apply with this deployment.' : 'Suggested from the manifest for a new target. Clear anything you do not want exposed.'}>
          {!current ? (
            <SkeletonLines lines={3} />
          ) : (
            <div className="space-y-5">
              <div>
                <h3 className="mb-2 text-sm font-semibold">Public hostnames (HTTPS through the managed proxy)</h3>
                <RoutesEditor routes={current.routes} services={services} defaults={defaultHosts} onChange={(routes) => setSettings({ ...current, routes })} />
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold">Host ports</h3>
                <PortsEditor ports={current.ports} services={services} onChange={(ports) => setSettings({ ...current, ports })} />
              </div>
              <details className="collapse collapse-arrow rounded-field border border-hairline">
                <summary className="collapse-title min-h-0 py-3 text-sm font-semibold">Resource limits</summary>
                <div className="collapse-content">
                  <ResourcesEditor resources={current.resources} services={services} onChange={(resources) => setSettings({ ...current, resources })} />
                </div>
              </details>
              {incomplete ? <p className="text-sm dr-ink-warning">Complete or remove the highlighted rows before deploying.</p> : null}
            </div>
          )}
        </Section>
      </div>

      <div className="min-w-0 xl:col-span-3">
        <Section
          title="Preview"
          description="Checked by the server with the same rules as the CLI. Nothing changes until you deploy."
          actions={preview.isFetching || !upToDate ? <span className="loading loading-spinner loading-sm" aria-label="Updating preview" /> : null}
          className="xl:sticky xl:top-20"
        >
          {!request ? <p className="text-sm text-base-content/60">Choose a branch, an environment and a VPS.</p> : null}
          {preview.isError ? <ErrorAlert error={preview.error} title="The preview could not be computed" onRetry={() => void preview.refetch()} /> : null}
          {request && !p && preview.isPending ? <SkeletonLines lines={6} /> : null}
          {p ? (
            <div className={`transition-opacity duration-300 ease-soft ${!upToDate || preview.isFetching ? 'opacity-60' : ''}`}>
              <DeployPreviewPanel p={p} stack={stackName(key, env)} configHref={configHref} />
            </div>
          ) : null}
          {p?.replacesDifferentRelease ? (
            <label className="flex animate-rise cursor-pointer items-start gap-3 rounded-field border border-warning/50 bg-warning/10 p-3.5">
              <input type="checkbox" className="checkbox checkbox-warning checkbox-sm mt-0.5" checked={confirmReplace} onChange={(e) => setConfirmReplace(e.target.checked)} />
              <span className="text-sm">
                I understand this replaces the running release
                {p.existing?.release ? (
                  <>
                    {' '}
                    <span className="font-mono">{p.existing.release.branch}@{p.existing.release.shortSha}</span>
                  </>
                ) : null}{' '}
                with <span className="font-mono">{p.branch ? `${p.branch.name}@${shortSha(p.branch.commitSha)}` : branch}</span>.
              </span>
            </label>
          ) : null}
          <ErrorAlert error={submit.error && !(submit.error instanceof ApiError && submit.error.confirmation) ? submit.error : null} title="The deployment was not started" />
          <div className="flex flex-wrap items-center justify-end gap-3">
            {p && !p.canDeploy ? <span className="text-sm dr-ink-error">Resolve the problems above to deploy.</span> : null}
            <Haptic disabled={blocked} className="max-sm:w-full">
              <button type="button" className="btn btn-primary max-sm:w-full" disabled={blocked} onClick={() => deploy()}>
                {submit.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : <Rocket className="size-4" aria-hidden="true" />}
                Deploy {branch ? <span className="font-mono">{branch}</span> : null} to {env || '…'}
              </button>
            </Haptic>
          </div>
          <p className="text-xs text-base-content/60 sm:text-right">
            The job runs on the server; you can follow it on the job page or with <code className="font-mono">devrobot status</code>. <Link className="link" to="/jobs?active=1">Active jobs</Link>
          </p>
        </Section>
      </div>

      {needsConfirmation ? (
        <ConfirmDialog
          title="Confirmation required"
          confirmLabel="Confirm and deploy"
          tone="warning"
          typedConfirmation={needsConfirmation.expected && needsConfirmation.expected !== 'true' ? needsConfirmation.expected : undefined}
          busy={submit.isPending}
          onCancel={() => setNeedsConfirmation(null)}
          onConfirm={() => {
            const { field, expected } = needsConfirmation;
            setNeedsConfirmation(null);
            if (field === 'confirmReplace') setConfirmReplace(true);
            deploy({ [field]: field === 'confirmReplace' ? true : (expected ?? true) } as Partial<DeployRequest>);
          }}
        >
          <p>{needsConfirmation.message}</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
